import { isBothHanded, resolveItemModelId, resolveOffHandPresentation, type CharacterEquipmentLoadout, type Equipment, type HandsItem, type ItemRarity } from '@sorrem/shared';
import { useCallback, useMemo, type ReactNode } from 'react';
import type { UIInventoryItem } from '@sorrem-client/types';
import { ItemIcon, LOADOUT_SLOT_KEYS, useItemTransferContext, type LoadoutSlotKey } from '@sorrem-client/ui/components';
import { EquipmentSummary } from '@sorrem-client/ui/features/ActionSlotSummary';
import { toUIInventoryItem } from '@sorrem-client/ui/shared/inventory-item';
import { toEquipmentSlotIndex, toEquipmentSlots, toHighlightedEquipmentSlotIndices } from '@sorrem-client/ui/shared/equipment-slots';
import styles from './loadout-equipment.module.scss';

/** What an equipped item's icon needs: its hands (model, off-hand model, grip) and its name. */
type EquippedItem = HandsItem & Partial<Pick<Equipment, 'name'>>;

/** The icon for an equipped item. */
export function EquippedItemIcon({ item }: { readonly item: EquippedItem }): JSX.Element {
    return <ItemIcon modelId={resolveItemModelId(item)} alt={item.name} />;
}

/**
 * The off-hand frame's icon: its own item, or beside a weapon that takes
 * both hands, that weapon's off-hand side (see `resolveOffHandPresentation`):
 * a two-handed weapon (or a bow's quiver) faded, a paired set's other half
 * in full. Nothing when the off hand is empty and free.
 */
export function OffHandItemIcon({ mainHand, offHand }: { readonly mainHand: EquippedItem | null | undefined; readonly offHand: EquippedItem | null | undefined }): JSX.Element | null {
    const presentation = resolveOffHandPresentation(mainHand);
    if (presentation === undefined) {
        return offHand ? <EquippedItemIcon item={offHand} /> : null;
    }
    return (
        <ItemIcon
            modelId={presentation.modelId}
            alt={mainHand?.name}
            className={presentation.isFaded ? styles.faded : undefined}
        />
    );
}

/** What each equipment and accessory frame shows, from the 7 slots (see `toEquipmentSlots`); null for an empty frame. */
export function buildEquipmentSlotContents(equipmentSlots: ReadonlyArray<Equipment | null>): Pick<LoadoutEquipmentProps, 'primary' | 'offHand' | 'armor' | 'accessories'> {
    const icon = (item: Equipment | null | undefined): JSX.Element | null => (item ? <EquippedItemIcon item={item} /> : null);
    const [mainHand, offHand] = equipmentSlots;
    return {
        primary: icon(mainHand),
        offHand: offHand || resolveOffHandPresentation(mainHand) !== undefined ? <OffHandItemIcon mainHand={mainHand} offHand={offHand} /> : null,
        armor: icon(equipmentSlots[2]),
        accessories: equipmentSlots.slice(3).map(icon),
    };
}

/**
 * What each equipment and accessory frame holds, for the popover shown while
 * the pointer rests on one: the item's card. Beside a weapon that takes both
 * hands, the off-hand frame shows that weapon's card, as it shows its icon.
 *
 * @param equipmentSlots The equipped items in 7-slot order (see `toEquipmentSlots`).
 */
export function buildEquipmentDetails(equipmentSlots: ReadonlyArray<Equipment | null>): Partial<Record<LoadoutSlotKey, ReactNode>> {
    const details: Partial<Record<LoadoutSlotKey, ReactNode>> = {};
    const mainHand = equipmentSlots[0];
    LOADOUT_SLOT_KEYS.forEach((slot, index) => {
        const item = slot === 'secondary' && resolveOffHandPresentation(mainHand) !== undefined ? mainHand : equipmentSlots[index];
        // An empty frame has nothing to describe.
        if (item) {
            details[slot] = <EquipmentSummary item={toUIInventoryItem(item)} />;
        }
    });
    return details;
}

/**
 * The rarity of what each equipment and accessory frame holds (#341). Beside
 * a weapon that takes both hands, the off-hand frame shows none: the weapon's
 * tier is on the main-hand frame.
 *
 * @param equipmentSlots The equipped items in 7-slot order (see `toEquipmentSlots`).
 */
export function buildEquipmentSlotRarities(equipmentSlots: ReadonlyArray<Equipment | null>): Partial<Record<LoadoutSlotKey, ItemRarity>> {
    const rarities: Partial<Record<LoadoutSlotKey, ItemRarity>> = {};
    LOADOUT_SLOT_KEYS.forEach((slot, index) => {
        const rarity = equipmentSlots[index]?.instance?.rarity;
        if (rarity !== undefined) {
            rarities[slot] = rarity;
        }
    });
    return rarities;
}

interface LoadoutEquipmentTransfer {
    /** A drag onto the frame: places the dragged (selected) item there. */
    onSlotDrop: (slot: LoadoutSlotKey) => void;
    /** A click on the frame: selects what it holds, or clears it if already selected. */
    onSlotSelect: (slot: LoadoutSlotKey) => void;
    /** Opens the frame's item menu (#267); undefined where the page has none. */
    onSlotMenu: ((slot: LoadoutSlotKey, anchor: HTMLElement) => void) | undefined;
    highlightedSlots: ReadonlyArray<LoadoutSlotKey>;
    selectedSlots: ReadonlyArray<LoadoutSlotKey>;
}

/** Beside a weapon that takes both hands, selecting it outlines both hand frames. */
const BOTH_HAND_SLOTS: ReadonlyArray<LoadoutSlotKey> = ['primary', 'secondary'];

/**
 * Binds a Loadout's equipment/accessory frames to the surrounding
 * ItemTransferProvider. Clicking a frame selects its item (again, clears it);
 * dragging an item onto a frame moves it there; right-clicking opens the
 * item's menu (#267).
 *
 * @param equipmentSlots The equipped items in 7-slot order (see `toEquipmentSlots`).
 * @param selectedEquipmentType Equipment type of the selected item, used to highlight valid frames.
 * @param selectedTakesBothHands Whether the selected item takes both hands, so only the main hand is valid.
 */
export function useLoadoutEquipmentTransfer(
    equipmentSlots: ReadonlyArray<EquippedItem | null>,
    selectedEquipmentType: UIInventoryItem['equipmentType'] | undefined,
    selectedTakesBothHands = false,
): LoadoutEquipmentTransfer {
    const {
        selectedItemId,
        toggleSelectedItem,
        clearSelectedItem,
        moveTo,
        openItemMenu,
    } = useItemTransferContext();

    const highlightedSlots = useMemo(
        () => toHighlightedEquipmentSlotIndices(selectedEquipmentType, selectedTakesBothHands).flatMap((index) => LOADOUT_SLOT_KEYS[index] ?? []),
        [selectedEquipmentType, selectedTakesBothHands],
    );
    // Beside a weapon that takes both hands, the off-hand frame stands for that weapon.
    const holdsBothHands = isBothHanded(equipmentSlots[0]);
    const selectedSlots = useMemo(() => {
        const selectedIndex = selectedItemId === null ? -1 : equipmentSlots.findIndex((entry) => entry?.id === selectedItemId);
        if (selectedIndex === 0 && holdsBothHands) {
            return BOTH_HAND_SLOTS;
        }
        const selectedKey = LOADOUT_SLOT_KEYS[selectedIndex];
        return selectedKey === undefined ? [] : [selectedKey];
    }, [equipmentSlots, holdsBothHands, selectedItemId]);

    // Beside a weapon that takes both hands, the off-hand frame stands for that weapon.
    const findSlotItem = useCallback((slotIndex: number): EquippedItem | null | undefined => (
        equipmentSlots[slotIndex === 1 && holdsBothHands ? 0 : slotIndex]
    ), [equipmentSlots, holdsBothHands]);

    const onSlotDrop = useCallback((slot: LoadoutSlotKey): void => {
        const slotIndex = toEquipmentSlotIndex(slot);
        if (selectedItemId !== null && findSlotItem(slotIndex)?.id !== selectedItemId) {
            moveTo('equipment', slotIndex);
        }
    }, [findSlotItem, moveTo, selectedItemId]);

    const onSlotSelect = useCallback((slot: LoadoutSlotKey): void => {
        const slotItem = findSlotItem(toEquipmentSlotIndex(slot));
        if (slotItem?.id) {
            toggleSelectedItem(slotItem.id, 'equipment');
        } else {
            clearSelectedItem();
        }
    }, [clearSelectedItem, findSlotItem, toggleSelectedItem]);

    const onSlotMenu = useCallback((slot: LoadoutSlotKey, anchor: HTMLElement): void => {
        const slotItem = findSlotItem(toEquipmentSlotIndex(slot));
        if (slotItem?.id && openItemMenu !== undefined) {
            openItemMenu(slotItem.id, 'equipment', anchor);
        }
    }, [findSlotItem, openItemMenu]);

    return { onSlotDrop, onSlotSelect, onSlotMenu: openItemMenu === undefined ? undefined : onSlotMenu, highlightedSlots, selectedSlots };
}

/** A `Loadout`'s equipment frame props: what each frame shows and how it takes part in item transfer. */
export interface LoadoutEquipmentProps extends LoadoutEquipmentTransfer {
    slotDetails: Partial<Record<LoadoutSlotKey, ReactNode>>;
    slotRarities: Partial<Record<LoadoutSlotKey, ItemRarity>>;
    primary: ReactNode;
    offHand: ReactNode;
    armor: ReactNode;
    accessories: ReactNode[];
}

const NO_EQUIPMENT_SLOTS: ReadonlyArray<Equipment | null> = [];

/**
 * Everything a `Loadout`'s equipment frames need, the same in a run, at a
 * gathering and in Character Home: the equipped items drawn in their
 * frames with their details, bound to the surrounding ItemTransferProvider.
 * The selected item is looked for among `selectableItems` (the page's
 * carried and, where it has them, stored items), then in the loadout.
 * Spread the result onto the `Loadout`.
 */
export function useLoadoutEquipment(
    equipped: CharacterEquipmentLoadout | null | undefined,
    selectableItems: ReadonlyArray<UIInventoryItem | undefined>,
): LoadoutEquipmentProps {
    const { selectedItemId } = useItemTransferContext();
    const equipmentSlots = useMemo(() => (equipped ? toEquipmentSlots(equipped) : NO_EQUIPMENT_SLOTS), [equipped]);
    const slotDetails = useMemo(() => buildEquipmentDetails(equipmentSlots), [equipmentSlots]);
    const slotRarities = useMemo(() => buildEquipmentSlotRarities(equipmentSlots), [equipmentSlots]);
    const selectedItem = selectedItemId === null
        ? undefined
        : selectableItems.find((entry) => entry?.id === selectedItemId) ?? equipmentSlots.find((entry) => entry?.id === selectedItemId);
    const transfer = useLoadoutEquipmentTransfer(equipmentSlots, selectedItem?.equipmentType, isBothHanded(selectedItem));
    return {
        ...transfer,
        slotDetails,
        slotRarities,
        ...buildEquipmentSlotContents(equipmentSlots),
    };
}
